/**
 * API-client implementation of the Repository (ARCHITECTURE §3.2, §12). This is
 * the production store: it calls the Cloudflare Pages Functions API (same-origin
 * `/api/*`), which is backed by D1 and enforces auth, RBAC, and tenant isolation
 * on the server. The Repository *interface* is unchanged, so `state/` and the UI
 * are identical to the local-first build — the swap is invisible above the seam.
 *
 * Auth is server-owned: the session token lives in an HttpOnly cookie the client
 * can't read, so this class also implements `AuthRepository` (sign-in / sign-out
 * / first-run setup) which the store delegates to. Reads made before sign-in
 * return 401; those are treated as "no data yet" so the sign-in gate renders
 * cleanly rather than surfacing an error.
 */
import type { Account, Entity, JournalEntry, TaxSettings } from "@core/index";
import type { AppMeta, AuthRepository, Repository, SetupRequest } from "./repository";
import { DEFAULT_META } from "./repository";
import type { Session, User } from "./identity";
import { buildInitialSettings } from "./seed";

async function toError(res: Response): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return new Error(body.error);
  } catch {
    /* non-JSON body */
  }
  return new Error(`Request failed (${res.status}).`);
}

export class ApiRepository implements Repository, AuthRepository {
  readonly kind = "api";

  private async call(path: string, method = "GET", body?: unknown): Promise<Response> {
    const init: RequestInit = { method, credentials: "same-origin" };
    if (body !== undefined) {
      init.headers = { "Content-Type": "application/json" };
      init.body = JSON.stringify(body);
    }
    return fetch(`/api${path}`, init);
  }

  /** GET a list; an unauthenticated 401 means "not signed in yet" → empty. */
  private async list<T>(path: string): Promise<T[]> {
    const res = await this.call(path);
    if (res.status === 401) return [];
    if (!res.ok) throw await toError(res);
    return (await res.json()) as T[];
  }

  /** GET one; 401 → null. */
  private async getOne<T>(path: string): Promise<T | null> {
    const res = await this.call(path);
    if (res.status === 401) return null;
    if (!res.ok) throw await toError(res);
    return (await res.json()) as T;
  }

  /** A mutating call that must succeed. */
  private async send(path: string, method: string, body?: unknown): Promise<void> {
    const res = await this.call(path, method, body);
    if (!res.ok) throw await toError(res);
  }

  async init(): Promise<void> {
    // Schema lives in D1 migrations; nothing to initialize client-side.
  }

  getEntities(): Promise<Entity[]> {
    return this.list<Entity>("/entities");
  }
  saveEntity(entity: Entity): Promise<void> {
    return this.send("/entities", "POST", entity);
  }

  getAccounts(): Promise<Account[]> {
    return this.list<Account>("/accounts");
  }
  saveAccount(account: Account): Promise<void> {
    return this.send("/accounts", "POST", account);
  }

  getEntries(): Promise<JournalEntry[]> {
    return this.list<JournalEntry>("/entries");
  }
  saveEntry(entry: JournalEntry): Promise<void> {
    return this.send("/entries", "POST", entry);
  }
  deleteEntry(id: string): Promise<void> {
    return this.send(`/entries/${encodeURIComponent(id)}`, "DELETE");
  }

  async getSettings(): Promise<TaxSettings> {
    const settings = await this.getOne<TaxSettings>("/settings");
    return settings ?? buildInitialSettings("AED");
  }
  saveSettings(settings: TaxSettings): Promise<void> {
    return this.send("/settings", "PUT", settings);
  }

  async getMeta(): Promise<AppMeta> {
    // /api/meta is public and always answers (it gates first render).
    const meta = await this.getOne<AppMeta>("/meta");
    return meta ?? DEFAULT_META;
  }
  saveMeta(meta: AppMeta): Promise<void> {
    return this.send("/meta", "PUT", meta);
  }

  getUsers(): Promise<User[]> {
    return this.list<User>("/users");
  }
  saveUser(user: User): Promise<void> {
    return this.send("/users", "POST", user);
  }
  deleteUser(id: string): Promise<void> {
    return this.send(`/users/${encodeURIComponent(id)}`, "DELETE");
  }

  async getSession(): Promise<Session | null> {
    const body = await this.getOne<{ session: Session | null }>("/auth/session");
    return body?.session ?? null;
  }
  async saveSession(session: Session | null): Promise<void> {
    // Sessions are server-owned; the client can only end one (sign out).
    if (session === null) await this.signOut();
  }

  reset(): Promise<void> {
    return this.send("/reset", "POST");
  }

  // --- AuthRepository (server-enforced authentication) ---

  async signIn(email: string, password: string): Promise<Session | null> {
    const res = await this.call("/auth/signin", "POST", { email, password });
    if (res.status === 401) return null;
    if (!res.ok) throw await toError(res);
    const { session } = (await res.json()) as { session: Session };
    return session;
  }

  async signOut(): Promise<void> {
    await this.call("/auth/signout", "POST", {});
  }

  async completeSetup(req: SetupRequest): Promise<Session> {
    const res = await this.call("/setup", "POST", req);
    if (!res.ok) throw await toError(res);
    const { session } = (await res.json()) as { session: Session };
    return session;
  }
}
